import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import emptyProfile from "@/images/empty_profile.png";
import {
  avatarEditSx,
  avatarSx,
  avatarWrapPublicSx,
  avatarWrapSx,
  railSectionSx,
} from "../styles";

interface ProfileHeaderProps {
  isUsersProfile: boolean;
  /** Opens the avatar upload modal owned by the Profile page. */
  open: () => void;
}

const ProfileHeader = ({ isUsersProfile, open }: ProfileHeaderProps) => {
  const { t } = useTranslation(["translation.Profile"]);

  // shallowEqual because this selector builds a fresh object: useSelector
  // compares by reference where connect() shallow-compared for us.
  const { name, avatarUrl, locale, purposeLabel } = useSelector((state: any) => {
    if (!isUsersProfile) {
      const { browseProfile } = state.browse;
      return {
        name: `${browseProfile.getIn(["user", "first_name"])} ${browseProfile.getIn(
          ["user", "last_name"]
        )}`,
        avatarUrl: browseProfile.get("avatar") || emptyProfile,
        locale: browseProfile.get("location_map")
          ? browseProfile.getIn(["location_map", "descrption"])
          : null,
        purposeLabel: browseProfile.getIn(["purpose", "label"]),
      };
    }

    const { avatar, location, purpose } = state.profile;
    return {
      name: `${state.user.get("first_name")} ${state.user.get("last_name")}`,
      avatarUrl: avatar || emptyProfile,
      locale: location ? location.get("description") : null,
      purposeLabel: purpose && purpose.get("label"),
    };
  }, shallowEqual);

  const renderAvatar = () => {
    if (!isUsersProfile) {
      return (
        <Box sx={avatarWrapPublicSx}>
          <Box component="img" src={avatarUrl} alt="avatar" sx={avatarSx} />
        </Box>
      );
    }

    // the whole picture is the hit area; the chip is the visible affordance
    return (
      <Box sx={avatarWrapSx} onClick={open} role="presentation">
        <Box component="img" src={avatarUrl} alt="avatar" sx={avatarSx} />
        <Button
          type="button"
          onClick={open}
          variant="contained"
          size="small"
          disableElevation
          sx={avatarEditSx}
        >
          {t("updatePic")}
        </Button>
      </Box>
    );
  };

  return (
    <Box component="section">
      {renderAvatar()}
      <Stack spacing={0.5} sx={{ ...railSectionSx, textAlign: "center" }}>
        {/* the theme's h1..h4 variants set textAlign: left, so it is restated */}
        <Typography variant="h2" sx={{ color: "common.white", textAlign: "center" }}>
          {name}
        </Typography>
        {purposeLabel && (
          <Typography variant="body1" sx={{ color: "common.white" }}>
            {purposeLabel}
          </Typography>
        )}
        {locale && (
          <Typography variant="body2" sx={{ color: "grey.400" }}>
            {locale}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ProfileHeader;
